import React, { useContext, useMemo } from 'react'
import {
  Accordion,
  AccordionContext,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Logo,
  Row,
  Spinner
} from 'react-angle-dashboard-components'
import { useHistory } from 'react-router-dom'
import { PDFDownloadLink } from '@react-pdf/renderer'

import { CardDescriptionText, CardHeaderText, CardValueText } from './style'
import { convertStringToTitleCase } from '@angle/utils'
import { ReactComponent as DocumentIcon } from './document.svg'
import BooleanRow from './BooleanRow'
import { Paths } from '../../routes/paths'
import { ICensusMemberDTO, IQuoteStatus, IRateStatus } from '../../services/quote/dto'

import { ReactComponent as ExclaimationCircle } from '../../assets/exclaimationCircle.svg'
import { ReactComponent as ExclaimationTriangle } from '../../assets/exclaimationTriangle.svg'
import { ReactComponent as InfoCircle } from '../../assets/infoCircle.svg'
import { ReactComponent as LoadingClock } from '../../assets/clock.svg'
import QuoteProposalPDF from '../QuoteProposalPDF/QuoteProposalPDF'

const hasValidRates = (plans: IPlan[]) => {
  return plans.every((plan) => plan.rates)
}

interface IRate {
  name: string
  employeeOnly: string
  employeePlusSpouse: string
  employeePlusChildren: string
  employeePlusFamily: string
}

interface IPlan {
  deductibleType: string
  famDeductible: string
  famOopMax: string
  id: string
  innDeductible: string
  innOopMax: string
  isCreditable: boolean
  isHsa: boolean
  name: string
  OON: {
    famDeductible: string
    OOPMFam: string
    indDeductible: string
    OOPMInd: string
    coinsurance: string
    deductibleEmbedded: boolean
  }
  rates?: IRate
  services: {
    default: string
    primaryCare: string
    specialist: string
    inPatient: string
    rxTier1: string
    rxTier2: string
    rxTier3: string
    rxTier4: string
    diagnostic: string
    urgentCare: string
    emergencyRoom: string
  }
  type: string
}

interface Agency {
  id: number
  name: string
}

export interface IQuoteView {
  addOns: {
    name: string;
    category: string;
    image: string;
  }[];
  network: {
    name: string;
    image: string;
  };
  pharmacy: {
    name: string;
    image: string;
  };
  plans: IPlan[];
  previousInfo: {
    planAdded: boolean;
    experienceProvided: boolean;
    invoiceInfoAdded: boolean;
  };
  previousQuote: number;
  quoteDetails: {
    groupName: string
    platformQuoted: string
    effectiveDate: string
    agency: Agency
    fundingType: string
    cityAndState: string
    zipCode: string
    pepmPayable?: number | null;
  };
  quoteId: string;
}

const mapQuoteStatusToBadge = (status: IQuoteStatus) => {
  switch (status) {
    case 'draft':
      return <Badge variant="secondary">Draft</Badge>
    case 'in_review':
      return <Badge variant="warning">In Review</Badge>
    case 'quoted':
      return <Badge variant="info">Quoted</Badge>
    case 'proposal':
      return <Badge variant="primary">Proposal</Badge>
    case 'sold':
      return <Badge variant="success">Sold</Badge>
    case 'archived':
      return <Badge variant="secondary">Archived</Badge>
    case 'declined':
      return <Badge variant="danger">Declined</Badge>
    case 'error':
      return <Badge variant="danger">Error</Badge>
  }
}

interface IQuoteViewProps {
  quote: IQuoteView
  quoteStatus: IQuoteStatus
  ratesStatus?: IRateStatus
  overrideShowRates?: boolean // If True, force rates card to not show;
  census?: ICensusMemberDTO[]
}

const CaretIcon = ({ myKey }: { myKey: string }) => {
  const currentEventKey = useContext(AccordionContext)
  return (
    <i
      className="icon fs-10 icon-dropdown"
      style={currentEventKey === myKey ? { transform: 'rotate(180deg)' } : {}}
    />
  )
}

const QuoteView = ({
  quote,
  quoteStatus,
  ratesStatus,
  overrideShowRates = false,
  census = []
}: IQuoteViewProps) => {
  const history = useHistory()

  const canEdit = quoteStatus === 'draft' || quoteStatus === 'quoted'

  const showRates =
    quoteStatus === 'in_review' ||
    quoteStatus === 'quoted' ||
    quoteStatus === 'proposal' ||
    quoteStatus === 'sold' ||
    quoteStatus === 'declined'

  const canDownloadQuote = quoteStatus === 'proposal' || quoteStatus === 'sold'
  const canDownloadPlans = false // disabled for MVP

  const onDownloadPlan = () => {
    console.log('TODO: non MVP')
  }

  const tiersAmount: Record<string, number> = useMemo(() => {
    return census.reduce((acc, item) => {
      if (!item.familyTier) {
        return acc
      }
      // @ts-ignore
      acc[item.familyTier!] = acc[item.familyTier!] || 0;
      // @ts-ignore
      acc[item.familyTier!]++

      return acc;
    }, {})
  }, [census])

  const internalRateCardElement = useMemo(() => {
    if (ratesStatus === 'not_started' || !ratesStatus) {
      return (
        <div className="d-flex flex-column align-items-center pt-4">
          <Spinner animation="border" variant="primary" className="mb-2" />
          <span
            style={{
              fontSize: 20,
              fontWeight: 500,
              color: '#171725',
              marginBottom: 4
            }}
          >
            Calculating Your Rates
          </span>
          <span
            style={{
              fontSize: 16,
              fontWeight: 400,
              color: '#6C757D',
              textAlign: 'center'
            }}
          >
            Remain on this page or check back shortly to review your
            personalized rates.
          </span>
        </div>
      )
    } else if (quoteStatus === 'declined') {
      return (
        <Alert
          className="mt-3"
          style={{
            backgroundColor: '#DC354520'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <ExclaimationTriangle className="mr-3" />
              Rates are unavailable for this quote.
            </div>
          </div>
        </Alert>
      )
    } else if (ratesStatus === 'in_review' || ratesStatus === 'need_review') {
      return (
        <div className="d-flex flex-column align-items-center pt-4">
          <LoadingClock />
          <span
            style={{
              fontSize: 20,
              fontWeight: 500,
              color: '#171725',
              marginBottom: 4
            }}
          >
            Quote Under Review
          </span>
          <span
            style={{
              fontSize: 16,
              fontWeight: 400,
              color: '#6C757D',
              textAlign: 'center'
            }}
          >
            We are currently reviewing the information provided for your quote.
            Please allow 1-2 business days for our team to finalize and provide
            the quoted details. Kindly check back periodically for updates.
          </span>
        </div>
      )
    } else if (ratesStatus === 'completed') {
      return quote.plans?.map(({ rates }) =>
        rates ? (
          <Row key={rates.name} className="py-2">
            <Col>
              <CardHeaderText>{rates.name}</CardHeaderText>
            </Col>
            <Col>
              <CardValueText>{rates.employeeOnly}</CardValueText>
            </Col>
            <Col>
              <CardValueText>{rates.employeePlusSpouse}</CardValueText>
            </Col>
            <Col>
              <CardValueText>{rates.employeePlusChildren}</CardValueText>
            </Col>
            <Col>
              <CardValueText>{rates.employeePlusFamily}</CardValueText>
            </Col>
          </Row>
        ) : undefined
      )
    } else if (ratesStatus === 'error') {
      return (
        <div className="d-flex flex-column align-items-center pt-4">
          <ExclaimationCircle
            style={{
              fill: 'red'
            }}
          />
          <span
            style={{
              fontSize: 20,
              fontWeight: 500,
              color: '#171725',
              marginBottom: 4
            }}
          >
            Error
          </span>
          <span
            style={{
              fontSize: 16,
              fontWeight: 400,
              color: '#6C757D',
              textAlign: 'center'
            }}
          >
            Oops! We've encountered an issue receiving your rates.
            <br />
            Please{' '}
            <a
              href="https://form.jotform.com/231915511459053"
              target="_blank"
              rel="noreferrer"
            >
              contact our team
            </a>{' '}
            to report this issue.
          </span>
        </div>
      )
    }
  }, [quote.plans, quoteStatus, ratesStatus])

  return (
    <Container className="p-4 pb-10">
      {quoteStatus === 'draft' && !quote.previousInfo.planAdded && (
        <Alert
          style={{
            backgroundColor: '#FFF3CD'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <ExclaimationTriangle className="mr-3" />
              Your group may be eligible for better rates. Please complete the
              missing sections for our team to review.
            </div>
            <Button
              onClick={() =>
                history.push(Paths.quoteFormQuoteDetailsStep1(quote.quoteId))
              }
            >
              Add Previous Information
            </Button>
          </div>
        </Alert>
      )}
      {quoteStatus === 'declined' && (
        <Alert
          className="mt-3"
          style={{
            backgroundColor: '#DC354520'
          }}
        >
          <div className="d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center">
              <ExclaimationTriangle className="mr-3" />
              This group had been declined by our team.
            </div>
            <div
              className="ml-6"
              style={{
                color: '#6C757D',
                fontSize: 14
              }}
            >
              If you believe there may be additional supporting documentation,
              or questions on this quote, please contact{' '}
              <a href="mailto:quotes@anglehealth.com">
                quotes@anglehealth.com.
              </a>
            </div>
          </div>
        </Alert>
      )}
      <Row className="d-flex justify-content-between">
        <Col className="col-auto">
          <Row>
            <Col
              className="d-flex align-items-center"
              style={{ maxWidth: '76px' }}
            >
              <div className="d-flex">
                <div
                  style={{
                    borderRadius: '100%',
                    backgroundColor: '#EEEAF7',
                    padding: 22
                  }}
                >
                  <DocumentIcon style={{ width: 30, height: 30 }} />
                </div>
              </div>
            </Col>
            <Col
              style={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                maxWidth: '65%',
                marginLeft: '1rem'
              }}
            >
              <div
                style={{ whiteSpace: 'normal', fontWeight: 600, fontSize: 32 }}
              >
                {quote.quoteDetails.groupName}
              </div>
              <div className="ml-1">Quote ID: {quote.quoteId}</div>
            </Col>
            <Col className="col-auto d-flex align-items-center">
              <Row
                className="gx1 d-flex align-items-center"
                style={{ whiteSpace: 'nowrap' }}
              >
                {canDownloadQuote && hasValidRates(quote.plans) && (
                  <>
                    <Col>
                      <PDFDownloadLink
                        document={
                          <QuoteProposalPDF
                            ratesPageProps={{
                              quoteId: quote.quoteId,
                              groupName: quote.quoteDetails.groupName,
                              quoteDetails: {
                                effectiveDate: quote.quoteDetails.effectiveDate,
                                fundingType: quote.quoteDetails.fundingType,
                                contractTerms: '12 mo',
                                agency: quote.quoteDetails.agency.name,
                                cityState: quote.quoteDetails.cityAndState,
                                zipCode: quote.quoteDetails.zipCode,
                                pepmPayable: quote.quoteDetails.pepmPayable,
                              },
                              networkProgram: quote.network,
                              pbmProgram: quote.pharmacy,
                              addOnPrograms: quote.addOns,
                              rates: quote.plans.map((plan) => plan.rates as IRate),
                              tiersAmount,
                            }}
                            plans={quote.plans.map((plan) => ({
                              quoteId: quote.quoteId,
                              planId: plan.id,
                              name: plan.name,
                              rates: plan.rates as IRate,
                              innDeductible: plan.innDeductible,
                              OOPMInd: plan.innOopMax,
                              famDeductible: plan.famDeductible,
                              OOPMFam: plan.famOopMax,
                              isPartDCreditable: plan.isCreditable,
                              OON: {
                                innDeductible: plan.OON.indDeductible,
                                OOPMInd: plan.OON.OOPMInd,
                                famDeductible: plan.OON.famDeductible,
                                OOPMFam: plan.OON.OOPMFam,
                                coinsurance: plan.OON.coinsurance,
                                deductibleEmbedded: plan.OON.deductibleEmbedded
                              },
                              services: {
                                primary: {
                                  costShare: plan.services.primaryCare,
                                  deductibleApplies: plan.isHsa
                                },
                                specialist: {
                                  costShare: plan.services.specialist,
                                  deductibleApplies: plan.isHsa
                                },
                                virtual: {
                                  costShare: '$0',
                                  deductibleApplies: plan.isHsa
                                },
                                preventive: {
                                  costShare: '$0',
                                  deductibleApplies: false
                                },
                                urgent: {
                                  costShare: plan.services.urgentCare,
                                  deductibleApplies: plan.isHsa
                                },
                                emergency: {
                                  costShare: plan.services.emergencyRoom,
                                  deductibleApplies: true
                                },
                                inpatient: {
                                  costShare: plan.services.inPatient,
                                  deductibleApplies: true
                                },
                                generics: {
                                  costShare: plan.services.rxTier1,
                                  deductibleApplies: plan.isHsa
                                },
                                preferredBrand: {
                                  costShare: plan.services.rxTier2,
                                  deductibleApplies: plan.isHsa
                                },
                                nonPreferredBrand: {
                                  costShare: plan.services.rxTier3,
                                  deductibleApplies: plan.isHsa
                                },
                                specialtyHighCost: {
                                  costShare: plan.services.rxTier4,
                                  deductibleApplies: true
                                },
                                OPPro: {
                                  costShare: plan.services.default,
                                  deductibleApplies: true
                                },
                                mentalHealthPro: {
                                  costShare: plan.services.primaryCare,
                                  deductibleApplies: plan.isHsa
                                },
                                speechTherapyPro: {
                                  costShare: plan.services.specialist,
                                  deductibleApplies: plan.isHsa
                                },
                                physicalTherapyPro: {
                                  costShare: plan.services.specialist,
                                  deductibleApplies: plan.isHsa
                                },
                                labPro: {
                                  costShare: plan.services.diagnostic,
                                  deductibleApplies: plan.isHsa
                                },
                                xRaySpecialist: {
                                  costShare: plan.services.diagnostic,
                                  deductibleApplies: plan.isHsa
                                },
                                xRayPrimary: {
                                  costShare: plan.services.diagnostic,
                                  deductibleApplies: plan.isHsa
                                },
                                xRayUnclass: {
                                  costShare: plan.services.diagnostic,
                                  deductibleApplies: plan.isHsa
                                },
                                ImagingPro: {
                                  costShare: plan.services.diagnostic,
                                  deductibleApplies: plan.isHsa
                                },
                                outpatient: {
                                  costShare: plan.services.default,
                                  deductibleApplies: true
                                },
                                mentalHealthFac: {
                                  costShare: plan.services.default,
                                  deductibleApplies: true
                                },
                                speechTherapyFac: {
                                  costShare: plan.services.specialist,
                                  deductibleApplies: plan.isHsa
                                },
                                physicalTherapyFac: {
                                  costShare: plan.services.specialist,
                                  deductibleApplies: plan.isHsa
                                },
                                labFac: {
                                  costShare: plan.services.diagnostic,
                                  deductibleApplies: plan.isHsa
                                },
                                imagingFac: {
                                  costShare: plan.services.default,
                                  deductibleApplies: true
                                },
                                skilledNursing: {
                                  costShare: plan.services.default,
                                  deductibleApplies: true
                                },
                                unclassified: {
                                  costShare: plan.services.default,
                                  deductibleApplies: true
                                }
                              }
                            }))}
                          />
                        }
                        fileName={`${quote.quoteDetails.effectiveDate}_${quote.quoteDetails.groupName}_${quote.quoteId}.pdf`}
                      >
                        {({ loading }) => (
                          <Button
                            className="d-flex align-items-center"
                            variant="link"
                          >
                            {loading ? (
                              <>
                                <span>Creating PDF...</span>
                                <Spinner className="ml-2" animation="border" />
                              </>
                            ) : (
                              <>
                                <i
                                  className="icon icon-download fs-14 mr-2"
                                  style={{ height: '100%', width: '1rem' }}
                                />
                                <span>Download</span>
                              </>
                            )}
                          </Button>
                        )}
                      </PDFDownloadLink>
                    </Col>
                    {/* <Col>
                      <Button variant="outline-primary">
                        <ShareIcon
                          className="mr-2"
                          style={{ height: '100%', width: '1rem' }}
                        />
                        <span>Share via email</span>
                      </Button>
                    </Col> */}
                  </>
                )}
                <Col>
                  <h2>{mapQuoteStatusToBadge(quoteStatus)}</h2>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <Col className="mt-4 gy-3">
        { quote.previousQuote ? (<Row>
          <Card className="w-100 shadow-sm">
            <Card.Body className="gy-2">
              <Row>
                <Col>
                  <InfoCircle style={{ height: '1.5rem'}} />
                    {`This quote is a copy of Quote ID: ${quote.previousQuote}`}
                </Col>
                <Col style={{ display: 'flex', justifyContent: 'flex-end'}}>
                  <Button
                    className="d-flex align-items-center"
                    variant="link"
                    style={{ lineHeight: "0.5" }}
                    onClick={() =>
                      history.push(Paths.viewQuote(quote.previousQuote.toString()))
                    }
                  >
                    <span>View Original</span>
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Row>) : null}
        <Row>
          <Card className="w-100 shadow-sm">
            <Card.Header>
              <CardHeaderText>Quote details</CardHeaderText>
            </Card.Header>
            <Card.Body className="gy-2">
              <Row>
                <Col>
                  <CardValueText>
                    {quote.quoteDetails.effectiveDate}
                  </CardValueText>
                  <CardDescriptionText>Effective Date</CardDescriptionText>
                </Col>
                <Col>
                  <CardValueText>
                    {quote.quoteDetails.fundingType}
                  </CardValueText>
                  <CardDescriptionText>Funding Type</CardDescriptionText>
                </Col>
                <Col>
                  <CardValueText>
                    {quote.quoteDetails.platformQuoted}
                  </CardValueText>
                  <CardDescriptionText>Platform Quoted</CardDescriptionText>
                </Col>
              </Row>
              <Row>
                <Col>
                  <CardValueText>
                    {quote.quoteDetails.cityAndState}
                  </CardValueText>
                  <CardDescriptionText>City, State</CardDescriptionText>
                </Col>
                <Col>
                  <CardValueText>{quote.quoteDetails.zipCode}</CardValueText>
                  <CardDescriptionText>ZIP Code</CardDescriptionText>
                </Col>
                <Col />
              </Row>
            </Card.Body>
          </Card>
        </Row>
        <Row>
          <Col className="pl-0">
            <Card className="w-100 h-100 shadow-sm">
              <Card.Header>
                <CardHeaderText>Network & Pharmacy</CardHeaderText>
                {canEdit && (
                  <Button
                    onClick={() =>
                      history.push(Paths.basicInfoStep2(quote.quoteId))
                    }
                    variant="link"
                  >
                    Edit
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col className="col-auto">
                    <img
                      style={{
                        width: 80,
                        height: 50,
                        objectFit: 'contain'
                      }}
                      src={quote.network.image}
                      alt=""
                    />
                  </Col>
                  <Col className="d-flex justify-content-center flex-column">
                    <CardValueText>{quote.network.name}</CardValueText>
                    <CardDescriptionText>Network</CardDescriptionText>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col className="col-auto">
                    <img
                      style={{
                        width: 80,
                        height: 50,
                        objectFit: 'contain'
                      }}
                      src={quote.pharmacy.image}
                      alt=""
                    />
                  </Col>
                  <Col className="d-flex justify-content-center flex-column">
                    <CardValueText>{quote.pharmacy.name}</CardValueText>
                    <CardDescriptionText>
                      Pharmacy Benefits Manager
                    </CardDescriptionText>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col className="pr-0">
            <Card className="w-100 h-100 shadow-sm">
              <Card.Header>
                <CardHeaderText>Previous Information</CardHeaderText>
                {canEdit && (
                  <Button
                    onClick={() =>
                      history.push(
                        Paths.quoteFormQuoteDetailsStep1(quote.quoteId)
                      )
                    }
                    variant="link"
                  >
                    Edit
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                <Col className="pl-0 gy-3">
                  <BooleanRow
                    title="Previous Plan Added"
                    value={quote.previousInfo.planAdded}
                  />
                  <BooleanRow
                    title="Previous Experience Provided"
                    value={quote.previousInfo.experienceProvided}
                  />
                  <BooleanRow
                    title="Previous Invoice Info Added"
                    value={quote.previousInfo.invoiceInfoAdded}
                  />
                </Col>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {showRates && !overrideShowRates && (
          <Row>
            <Card className="w-100 shadow-sm">
              <Card.Header>
                <div className="d-flex align-items-center">
                  <Logo color="primary" />
                  <CardHeaderText className="mt-2">Rates</CardHeaderText>
                </div>
              </Card.Header>
              <Card.Body>
                <Row
                  className="py-2"
                  style={{
                    backgroundColor: '#F8F9FA'
                  }}
                  noGutters
                >
                  <Col />
                  <Col>
                    <CardDescriptionText>Employee Only</CardDescriptionText>
                  </Col>
                  <Col>
                    <CardDescriptionText>Employee + Spouse</CardDescriptionText>
                  </Col>
                  <Col>
                    <CardDescriptionText>
                      Employee + Child(ren)
                    </CardDescriptionText>
                  </Col>
                  <Col>
                    <CardDescriptionText>Employee + Family</CardDescriptionText>
                  </Col>
                </Row>
                <Row
                  className="py-2"
                  style={{
                    backgroundColor: '#F8F9FA'
                  }}
                  noGutters
                >
                  <Col>
                    Tier Count
                  </Col>
                  <Col>
                    <CardDescriptionText>{tiersAmount.single || '-'}</CardDescriptionText>
                  </Col>
                  <Col>
                    <CardDescriptionText>{tiersAmount.employee_spouse || '-'}</CardDescriptionText>
                  </Col>
                  <Col>
                    <CardDescriptionText>
                      {tiersAmount.employee_children}
                    </CardDescriptionText>
                  </Col>
                  <Col>
                    <CardDescriptionText>{tiersAmount.family || '-'}</CardDescriptionText>
                  </Col>
                </Row>
                {internalRateCardElement}
              </Card.Body>
            </Card>
          </Row>
        )}
        <Row>
          <Card className="w-100 shadow-sm">
            <Card.Header>
              <CardHeaderText>Plans Quoted</CardHeaderText>
              {canEdit && (
                <Button
                  onClick={() =>
                    history.push(Paths.quoteFormPlanInfoStep1(quote.quoteId))
                  }
                  variant="link"
                >
                  Edit
                </Button>
              )}
            </Card.Header>
            <Card.Body className="py-0">
              <Accordion defaultActiveKey={quote.plans?.[0]?.id}>
                {quote.plans.map((plan) => (
                  <div key={plan.id} className="border-bottom">
                    <Accordion.Toggle as="div" role="button" eventKey={plan.id}>
                      <Row
                        style={{ height: 60 }}
                        className="align-items-center justify-content-between"
                      >
                        <Col className="col-auto">
                          <div className="d-flex align-items-center">
                            {plan.name}
                            <CaretIcon myKey={plan.id} />
                          </div>
                        </Col>
                        {canDownloadPlans && (
                          <Col className="col-auto">
                            <Button variant="link" onClick={onDownloadPlan}>
                              <i
                                className="icon icon-download fs-14 mr-2"
                                style={{ height: '100%', width: '1rem' }}
                              />
                              <span>Download</span>
                            </Button>
                          </Col>
                        )}
                      </Row>
                    </Accordion.Toggle>
                    <Accordion.Collapse
                      className="pb-3 gy-2"
                      eventKey={plan.id}
                    >
                      <>
                        <Row>
                          <Col>
                            <CardValueText>{plan.type}</CardValueText>
                            <CardDescriptionText>Type</CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>{plan.deductibleType}</CardValueText>
                            <CardDescriptionText>
                              Deductible Type
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>{plan.innDeductible}</CardValueText>
                            <CardDescriptionText>
                              INN Deductible
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>{plan.innOopMax}</CardValueText>
                            <CardDescriptionText>
                              INN OOP Max
                            </CardDescriptionText>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <CardHeaderText>
                              Costshare for In-Network Services
                            </CardHeaderText>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <CardValueText>
                              {plan.services.default}
                            </CardValueText>
                            <CardDescriptionText>Default</CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.primaryCare}
                            </CardValueText>
                            <CardDescriptionText>
                              Primary Care
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.specialist}
                            </CardValueText>
                            <CardDescriptionText>
                              Specialist
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.inPatient}
                            </CardValueText>
                            <CardDescriptionText>
                              In Patient
                            </CardDescriptionText>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <CardValueText>
                              {plan.services.rxTier1}
                            </CardValueText>
                            <CardDescriptionText>
                              Pharmacy Tier 1
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.rxTier2}
                            </CardValueText>
                            <CardDescriptionText>
                              Pharmacy Tier 2
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.rxTier3}
                            </CardValueText>
                            <CardDescriptionText>
                              Pharmacy Tier 3
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.rxTier4}
                            </CardValueText>
                            <CardDescriptionText>
                              Pharmacy Tier 4
                            </CardDescriptionText>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <CardValueText>
                              {plan.services.diagnostic}
                            </CardValueText>
                            <CardDescriptionText>
                              Diagnostic
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.urgentCare}
                            </CardValueText>
                            <CardDescriptionText>
                              Urgent Care
                            </CardDescriptionText>
                          </Col>
                          <Col>
                            <CardValueText>
                              {plan.services.emergencyRoom}
                            </CardValueText>
                            <CardDescriptionText>
                              Emergency Room
                            </CardDescriptionText>
                          </Col>
                          <Col></Col>
                        </Row>
                      </>
                    </Accordion.Collapse>
                  </div>
                ))}
              </Accordion>
            </Card.Body>
          </Card>
        </Row>
        <Row>
          <Card className="w-100 shadow-sm">
            <Card.Header>
              <CardHeaderText>Add-ons</CardHeaderText>
              {canEdit && (
                <Button
                  onClick={() =>
                    history.push(Paths.quoteFormPlanInfoStep2(quote.quoteId))
                  }
                  variant="link"
                >
                  Edit
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {quote.addOns.map((addon) => (
                <React.Fragment key={addon.name}>
                  <Row>
                    <Col className="col-auto">
                      <img
                        style={{
                          width: 80,
                          height: 50,
                          objectFit: 'contain'
                        }}
                        src={addon.image}
                        alt=""
                      />
                    </Col>
                    <Col className="d-flex justify-content-center flex-column">
                      <CardValueText>{addon.name}</CardValueText>
                      <CardDescriptionText>
                        {convertStringToTitleCase(addon.category)}
                      </CardDescriptionText>
                    </Col>
                  </Row>
                  <hr />
                </React.Fragment>
              ))}
            </Card.Body>
          </Card>
        </Row>
      </Col>
    </Container>
  )
}

export default QuoteView
